import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import Select from '../../components/bootstrap/forms/Select';
import Card, {
	CardBody,
	CardHeader,
	CardLabel,
	CardTitle,
	CardActions,
} from '../../components/bootstrap/Card';
import Button from '../../components/bootstrap/Button';
import PageWrapper from '../../layout/PageWrapper/PageWrapper';
import Page from '../../layout/Page/Page';
import SubHeader, { SubHeaderLeft } from '../../layout/SubHeader/SubHeader';
import Icon from '../../components/icon/Icon';
import TableSpinner from '../../common/table/TableSpinner';
import { selectLoginResponse } from '../../redux/login/login.selector';
import { getMonthlyAttendanceSummaryReportStart } from '../../redux/NewReport/NewReport.actions';
import { selectMonthlyAttendanceSummaryReportResponse } from '../../redux/NewReport/NewReport.selectors';

interface AttendanceSummaryData {
	month: string;
	totalEmployees: number;
	workingDays: number;
	totalManDays: number;
	presentCount: number;
	absentCount: number;
	leaveCount: number;
	presentPercentage: string;
	absentPercentage: string;
	leavePercentage: string;
	isCurrentMonth?: boolean;
}

interface MonthOption {
	value: number;
	text: string;
}

// Add SVG pie chart helper
function getPieSegments(data: { label: string; value: number; color: string }[]) {
	const total = data.reduce((sum, d) => sum + d.value, 0) || 1;
	let cumulative = 0;
	return data.map((d, i) => {
		const startAngle = (cumulative / total) * 2 * Math.PI;
		cumulative += d.value;
		const endAngle = (cumulative / total) * 2 * Math.PI;
		const x1 = 100 + 100 * Math.sin(startAngle);
		const y1 = 100 - 100 * Math.cos(startAngle);
		const x2 = 100 + 100 * Math.sin(endAngle);
		const y2 = 100 - 100 * Math.cos(endAngle);
		const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;
		return (
			<path
				key={i}
				d={`M100,100 L${x1},${y1} A100,100 0 ${largeArc} 1 ${x2},${y2} Z`}
				fill={d.color}
				stroke='#fff'
				strokeWidth='2'
			/>
		);
	});
}

const MonthlyAttendanceSummaryReport = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const loginData = useSelector(selectLoginResponse);
	const reportDataResponse = useSelector(selectMonthlyAttendanceSummaryReportResponse);
	const [month, setMonth] = useState(new Date().getMonth() + 1); // Current month
	
	// Extract decrypted data from response
	const reportData = reportDataResponse?.data ?? null;
	useEffect(() => {
		if (!loginData?.organisationID) {
			navigate('/auth-pages/login');
			return;
		}
	}, [loginData, navigate]);

	const months: MonthOption[] = [
		{ value: 1, text: 'January' },
		{ value: 2, text: 'February' },
		{ value: 3, text: 'March' },
		{ value: 4, text: 'April' },
		{ value: 5, text: 'May' },
		{ value: 6, text: 'June' },
		{ value: 7, text: 'July' },
		{ value: 8, text: 'August' },
		{ value: 9, text: 'September' },
		{ value: 10, text: 'October' },
		{ value: 11, text: 'November' },
		{ value: 12, text: 'December' },
	];

	useEffect(() => {
		if (loginData?.organisationID) {
			dispatch(
				getMonthlyAttendanceSummaryReportStart({
					selectedMonth: month,
					organisationID: loginData.organisationID,
				}),
			);
		}
	}, [month, loginData?.organisationID, dispatch]);

	const handleExport = () => {
		if (!reportData) return;

		const csvData = [
			['Monthly Attendance Summary Report'],
			['Month', reportData.month],
			['Total Employees', reportData.totalEmployees],
			['Working Days', reportData.workingDays],
			['Total Man Days', reportData.totalManDays],
			['Present Count', reportData.presentCount],
			['Absent Count', reportData.absentCount],
			['Leave Count', reportData.leaveCount],
			['Present Percentage', `${reportData.presentPercentage}%`],
			['Absent Percentage', `${reportData.absentPercentage}%`],
			['Leave Percentage', `${reportData.leavePercentage}%`],
		];

		const csvContent = csvData.map((row) => row.join(',')).join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
		const link = document.createElement('a');
		const url = URL.createObjectURL(blob);
		link.setAttribute('href', url);
		link.setAttribute('download', `Monthly_Attendance_Summary_${reportData.month}_2025.csv`);
		link.style.visibility = 'hidden';
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const loading = reportData === null || reportData === undefined;
	const hasError = reportData && reportData.error;
	const data: AttendanceSummaryData | null =
		reportData &&
		!reportData.error &&
		typeof reportData === 'object' &&
		reportData.month !== undefined
			? reportData
			: null;

	return (
		<PageWrapper>
			<SubHeader>
				<SubHeaderLeft>
					<Icon icon='PieChart' size='2x' color='primary' />
					<span className='h4 mb-0 ms-2'>Monthly Attendance Summary Report</span>
				</SubHeaderLeft>
			</SubHeader>
			<Page container='fluid'>
				<div className='row'>
					<div className='col-12'>
						<Card stretch>
							<CardHeader>
								<CardLabel>
									<CardTitle>Report Filters</CardTitle>
								</CardLabel>
								<CardActions>
									<Button
										color='primary'
										onClick={handleExport}
										isDisable={loading || !data}>
										Export Report
									</Button>
								</CardActions>
							</CardHeader>
							<CardBody>
								<div className='row g-4 mb-4'>
									<div className='col-md-3'>
										<Select
											id='month'
											value={month.toString()}
											onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
												setMonth(Number(e.target.value))
											}
											ariaLabel='Select Month'>
											{months.map((monthOption) => (
												<option
													key={monthOption.value}
													value={monthOption.value}>
													{monthOption.text}
												</option>
											))}
										</Select>
									</div>
								</div>

								{loading ? (
									<TableSpinner />
								) : hasError ? (
									<div className='alert alert-danger'>
										Error loading data:{' '}
										{typeof hasError === 'string'
											? hasError
											: hasError?.message || 'Unknown error'}
									</div>
								) : !data ? (
									<div className='alert alert-info'>
										No data available for the selected month
									</div>
								) : (
									<>
										<div className='row'>
											{/* Horizontal Summary Table */}
											<div className='col-md-8'>
												<Card>
													<CardHeader>
														<CardTitle>
															Monthly Summary -{' '}
															{String(data.month || 'N/A')}
															{data.isCurrentMonth && (
																<small className='text-muted ms-2'>
																	(Current month data as of today)
																</small>
															)}
														</CardTitle>
													</CardHeader>
													<CardBody>
														<div className='table-responsive'>
															<table className='table table-modern table-hover text-center'>
																<thead>
																	<tr>
																		<th>Month</th>
																		<th>No. of employees</th>
																		<th>No. of working days</th>
																		<th>No. of man days</th>
																		<th>No. of days present</th>
																		<th>No. of days absent</th>
																		<th>% of present days</th>
																		<th>% of absent days</th>
																	</tr>
																</thead>
																<tbody>
																	<tr>
																		<td>
																			{String(
																				data.month || 'N/A',
																			)}
																		</td>
																		<td>
																			{String(
																				data.totalEmployees ||
																					0,
																			)}
																		</td>
																		<td>
																			{String(
																				data.workingDays ||
																					0,
																			)}
																		</td>
																		<td>
																			{String(
																				data.totalManDays ||
																					0,
																			)}
																		</td>
																		<td>
																			{String(
																				data.presentCount ||
																					0,
																			)}
																		</td>
																		<td>
																			{String(
																				data.absentCount ||
																					0,
																			)}
																		</td>
																		<td>
																			{String(
																				data.presentPercentage ||
																					'0',
																			)}
																			%
																		</td>
																		<td>
																			{String(
																				data.absentPercentage ||
																					'0',
																			)}
																			%
																		</td>
																	</tr>
																</tbody>
															</table>
														</div>
													</CardBody>
												</Card>
											</div>
											{/* Chart Side by Side */}
											<div className='col-md-4 d-flex align-items-stretch'>
												<Card
													style={{
														minWidth: 300,
														maxWidth: 400,
														width: '100%',
														margin: '0 auto',
													}}>
													<CardHeader>
														<CardTitle>
															Attendance Distribution
															{data.isCurrentMonth && (
																<small className='text-muted ms-2'>
																	(as of today)
																</small>
															)}
														</CardTitle>
													</CardHeader>
													<CardBody>
														<div className='text-center p-0 m-0'>
															<h5 style={{ marginBottom: 12 }}>
																Attendance Distribution
															</h5>
															{/* SVG Pie Chart */}
															<div
																style={{
																	width: 220,
																	height: 220,
																	margin: '0 auto',
																}}>
																<svg width={220} height={220}>
																	{getPieSegments([
																		{
																			label: 'Present',
																			value:
																				data?.presentCount ||
																				0,
																			color: '#4472C4',
																		},
																		{
																			label: 'Absent',
																			value:
																				data?.absentCount ||
																				0,
																			color: '#E07C24',
																		},
																		{
																			label: 'Leave',
																			value:
																				data?.leaveCount ||
																				0,
																			color: '#70AD47',
																		},
																	])}
																</svg>
															</div>
															<div
																style={{
																	marginTop: 8,
																	textAlign: 'center',
																}}>
																{[
																	{
																		label: 'Present',
																		value:
																			data?.presentCount || 0,
																		color: '#4472C4',
																	},
																	{
																		label: 'Absent',
																		value:
																			data?.absentCount || 0,
																		color: '#E07C24',
																	},
																	{
																		label: 'Leave',
																		value:
																			data?.leaveCount || 0,
																		color: '#70AD47',
																	},
																].map((d, i) => (
																	<span
																		key={i}
																		style={{
																			marginRight: 12,
																			verticalAlign: 'middle',
																		}}>
																		<span
																			style={{
																				display:
																					'inline-block',
																				width: 12,
																				height: 12,
																				background: d.color,
																				marginRight: 4,
																				borderRadius: 2,
																			}}
																		/>
																		{d.label} ({d.value})
																	</span>
																))}
															</div>
														</div>
													</CardBody>
												</Card>
											</div>
										</div>
									</>
								)}
							</CardBody>
						</Card>
					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default MonthlyAttendanceSummaryReport;
